import { useState, useEffect } from 'react';
import { BrandName } from '../main/BrandName';
import { useTranslation } from 'react-i18next';
import { Block } from '../common/blocks';
import { BLOCK } from '../../constants/common';

const ComingSoon = ({ text = 'Coming soon' }) => {
    const { t } = useTranslation();
    const [activeCircle, setActiveCircle] = useState(0);

    const colors = {
        black: '#000',
        dark: '#1a1a1a',
        light: '#eaeaea',
        lightgray: '#bfbfbf',
        gray: '#c3c2c2',
        darkgray: '#817e7e',
        yellowLight: '#ffff1a',
        yellow: '#FFB020',
        greenLight: '#1aff1a',
        green: '#10B981',
        darkredLight: '#ff1a1a',
        darkred: '#DC2626',
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveCircle((prev) => (prev + 1) % 3);
        }, 600);

        return () => clearInterval(interval);
    }, []);

    const handleCircleClick = (index) => {
        setActiveCircle(index);
    };

    const getCircleStyle = (index) => {
        const isActive = activeCircle === index;

        let backgroundColor; let boxShadow; let border;

        if (index === 0) {
            // Красный круг
            backgroundColor = isActive ? colors.darkred : colors.darkgray;
            boxShadow = isActive
                ? `0 0 20px ${colors.darkred}, 0 0 40px ${colors.darkred}, 0.2rem 0.2rem 0.5rem ${colors.dark}`
                : `0.2rem 0.2rem 0.5rem ${colors.dark}`;
            border = `3px solid ${isActive ? colors.darkred : colors.dark}`;
        } else if (index === 1) {
            // Желтый круг
            backgroundColor = isActive ? colors.yellow : colors.darkgray;
            boxShadow = isActive
                ? `0 0 20px ${colors.yellow}, 0 0 40px ${colors.yellow}, 0.2rem 0.2rem 0.5rem ${colors.dark}`
                : `0.2rem 0.2rem 0.5rem ${colors.dark}`;
            border = `3px solid ${isActive ? colors.yellow : colors.dark}`;
        } else {
            // Зеленый круг
            backgroundColor = isActive ? colors.green : colors.darkgray;
            boxShadow = isActive
                ? `0 0 20px ${colors.green}, 0 0 40px ${colors.green}, 0.2rem 0.2rem 0.5rem ${colors.dark}`
                : `0.2rem 0.2rem 0.5rem ${colors.dark}`;
            border = `3px solid ${isActive ? colors.green : colors.dark}`;
        }

        return {
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor,
            boxShadow,
            border,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: isActive ? 'scale(1.1)' : 'scale(1)',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        };
    };

    return (
        <div
            style={{
                fontFamily: 'Arial, sans-serif',
                minHeight: '75vh',
                marginBottom: '25vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                color: colors.light,
                overflow: 'hidden',
            }}
        >
            <style>
                {`
                    @keyframes glow {
                        from {
                            text-shadow:
                                0.2rem 0.2rem 0.5rem ${colors.dark},
                                0 0 10px ${colors.yellow};
                        }
                        to {
                            text-shadow:
                                0.2rem 0.2rem 0.5rem ${colors.dark},
                                0 0 20px ${colors.yellow},
                                0 0 30px ${colors.yellowLight};
                        }
                    }

                    @keyframes badgePulse {
                        0%, 100% {
                            transform: scale(1);
                        }
                        50% {
                            transform: scale(1.05);
                        }
                    }

                    .glow-text {
                        animation: glow 2s ease-in-out infinite alternate;
                    }

                    .pulse-badge {
                        animation: badgePulse 2s ease-in-out infinite;
                    }

                    @media (max-width: 768px) {
                        .main-title {
                            font-size: 2.5rem !important;
                        }
                        .circles-container {
                            gap: 30px !important;
                        }
                        .circle {
                            width: 70px !important;
                            height: 70px !important;
                        }
                    }

                    @media (max-width: 480px) {
                        .main-title {
                            font-size: 2rem !important;
                        }
                        .circles-container {
                            gap: 20px !important;
                        }
                        .circle {
                            width: 60px !important;
                            height: 60px !important;
                        }
                    }
                `}
            </style>

            <div
                style={{
                    textAlign: 'center',
                    position: 'relative',
                    width: '100%',
                    maxWidth: '600px',
                    padding: '20px',
                }}
            >
                {/* Заголовок */}
                <Block name={BLOCK.CENTER}>
                    <h1
                        className="main-title glow-text"
                        style={{
                            fontSize: '4rem',
                            fontWeight: 'bold',
                            color: colors.yellow,
                            marginBottom: '3rem',
                            textShadow: `0.2rem 0.2rem 0.5rem ${colors.dark}`,
                        }}
                    >
                        <BrandName />
                    </h1>
                </Block>

                {/* Три круга */}
                <div
                    className="circles-container"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '40px',
                        margin: '4rem 0',
                        position: 'relative',
                    }}
                >
                    {[0, 1, 2].map((index) => (
                        <div
                            key={index}
                            aria-hidden="true"
                            className="circle"
                            style={getCircleStyle(index)}
                            onClick={() => handleCircleClick(index)}
                        >
                            {/* Блик на круге */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '20%',
                                    left: '30%',
                                    width: '20px',
                                    height: '20px',
                                    background: 'rgba(255,255,255,0.3)',
                                    borderRadius: '50%',
                                    filter: 'blur(3px)',
                                    opacity: activeCircle === index ? 1 : 0.5,
                                    transition: 'opacity 0.3s ease',
                                }}
                            />
                        </div>
                    ))}
                </div>

                <div
                    className="pulse-badge"
                    style={{
                        background: colors.darkred,
                        color: colors.light,
                        padding: '5px 20px',
                        borderRadius: '5px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'inline-block',
                    }}
                >
                    {t(text)}
                </div>
            </div>
        </div>
    );
};

export default ComingSoon;
